import {
    Accordion,
    AccordionItem,
    AccordionItemContent,
    AccordionItemContext,
    AccordionItemHeader,
} from "@edgetier/client-components";
import VerifyPermission from "components-for/pages/verify-permission";
import { NavigationHierarchy } from "constants/routes/route-navigation-props";
import { ClientContext } from "context-for/client/client-context";
import { FunctionComponent, memo, useContext } from "react";
import { NavigationCategories } from "types-for/routes";
import classNames from "classnames";

import MobileNavigationLink from "../mobile-navigation-link";
import NavigationGroupHeader from "../navigation-group-header";
import "./mobile-navigation-accordion.scss";
import { IProps } from "./mobile-navigation-accordion.types";
import { isCurrentNavigationGroupActive } from "utilities-for/navigation/is-current-navigation-group-active";
import { useFilterNavigationChildren } from "hooks-for/navigation";

/**
 * The mobile navigation accordion.
 * @param props.isAgentReviewEnabled Whether agent review is enabled.
 * @param props.isExploreTagsEnabled Whether explore tags is enabled.
 */
const MobileNavigationAccordion: FunctionComponent<IProps> = () => {
    const client = useContext(ClientContext);
    const filterNavigationChildren = useFilterNavigationChildren();
    return (
        <Accordion>
            {(Object.keys(NavigationHierarchy) as Array<keyof typeof NavigationHierarchy>)
                .filter((key) => {
                    return key !== NavigationCategories.AgentReview || client.isAgentReviewEnabled;
                })
                .map((key) => {
                    const navigationItem = NavigationHierarchy[key];
                    if ("children" in navigationItem) {
                        const isGroupActive = isCurrentNavigationGroupActive(key);
                        return (
                            <VerifyPermission permission={navigationItem.permission} key={key}>
                                <AccordionItem canOpen isOpen={false}>
                                    <AccordionItemContext.Consumer>
                                        {(context) => (
                                            <AccordionItemHeader
                                                className={classNames("mobile-navigation-accordion__item-header", {
                                                    "mobile-navigation-accordion__item-header--active":
                                                        isGroupActive && !context?.isOpen,
                                                })}
                                            >
                                                <NavigationGroupHeader
                                                    icon={navigationItem.icon}
                                                    title={key}
                                                    isActive={isGroupActive}
                                                />
                                            </AccordionItemHeader>
                                        )}
                                    </AccordionItemContext.Consumer>
                                    <AccordionItemContent>
                                        <div className="mobile-navigation-accordion__item-content">
                                            {navigationItem.children
                                                .filter(({ pageName }) => filterNavigationChildren(pageName, client))
                                                .map((item) => (
                                                    <VerifyPermission permission={item.permission} key={item.label}>
                                                        <MobileNavigationLink {...item} />
                                                    </VerifyPermission>
                                                ))}
                                        </div>
                                    </AccordionItemContent>
                                </AccordionItem>
                            </VerifyPermission>
                        );
                    }
                    return (
                        <VerifyPermission permission={navigationItem.permission} key={key}>
                            <MobileNavigationLink {...navigationItem} />
                        </VerifyPermission>
                    );
                })}
        </Accordion>
    );
};

export default memo(MobileNavigationAccordion);
